// Granular freeze — take a small position window of a source buffer and
// stretch it indefinitely by scattering overlapping grains over the
// output. Pitch is preserved (each grain plays at native rate); only the
// "time at which the source is held" is suspended.
//
// Why scatter instead of repeat: a fixed loop becomes audibly periodic.
// Random grain offsets (within a small jitter range) mask the periodicity
// the ear would otherwise lock onto. Hanning-window each grain so they
// sum without clicks at the seams.
//
// Determinism: the PRNG is seedable, so calling freeze twice with the
// same options returns byte-identical output. Important so users can
// re-render a saved patch and get the same texture.

import type { PcmBuffer } from '../../pipeline';

export type FreezeOptions = {
  /** Centre of the position window, in seconds into the source. */
  positionSeconds: number;
  /** Window size — how much source material the grains can be drawn from.
   *  Smaller = more frozen / static; larger = more variation. The effective
   *  window is at least one grain length: requesting a smaller window can't
   *  actually scatter and is silently expanded. */
  windowSeconds: number;
  /** Length of the rendered output in seconds. */
  outputSeconds: number;
  /** Grain length in milliseconds. Default 80. */
  grainMs?: number;
  /** Overlap between successive grain starts, 0..1. Default 0.7 — high
   *  overlap is what hides the per-grain envelope. */
  overlap?: number;
  /** Seed for the PRNG. Default 0xc1c4 (matches the test fixture). */
  seed?: number;
};

export function applyFreeze(
  source: PcmBuffer,
  opts: FreezeOptions,
): PcmBuffer {
  const sr = source.sampleRate;
  const grainMs = opts.grainMs ?? 80;
  const overlap = clamp01(opts.overlap ?? 0.7);
  const seed = opts.seed ?? 0xc1c4;

  const grainLen = Math.max(64, Math.round((grainMs / 1000) * sr));
  const outLen = Math.max(1, Math.round(opts.outputSeconds * sr));
  const inLen = source.channels[0]?.length ?? 0;
  // Empty source → silence of the requested length. Returning 0-length
  // would surprise callers who set outputSeconds expecting a buffer of
  // that size.
  if (inLen === 0) {
    const channelCount = Math.max(1, source.channels.length);
    return {
      sampleRate: sr,
      channels: Array.from({ length: channelCount }, () => new Float32Array(outLen)),
    };
  }

  // Position window — clamp so grains can't read past either end of the
  // source. A grain starting at frame F reads up to F + grainLen, so the
  // latest legal start is inLen - grainLen.
  const windowFrames = Math.max(grainLen, Math.round(opts.windowSeconds * sr));
  const centre = Math.round(opts.positionSeconds * sr);
  const halfWindow = Math.floor(windowFrames / 2);
  const earliest = Math.max(0, centre - halfWindow);
  const latest = Math.min(Math.max(0, inLen - grainLen), centre + halfWindow);
  const range = Math.max(0, latest - earliest);

  const env = hanning(grainLen);
  const hop = Math.max(1, Math.round(grainLen * (1 - overlap)));
  const rng = mulberry32(seed);

  // Pre-compute the per-grain offsets ONCE so every channel reads from
  // the same source positions. Without this, stereo input collapses —
  // L and R each draw their own random offsets and the channels become
  // uncorrelated (audible as the stereo image dissolving into noise).
  const grainCount = Math.max(1, Math.ceil(outLen / hop));
  const offsets = new Int32Array(grainCount);
  for (let g = 0; g < grainCount; g++) {
    offsets[g] = range > 0 ? Math.floor(rng() * (range + 1)) : 0;
  }

  const channels = source.channels.map((srcCh) => {
    const chLen = srcCh.length;
    const out = new Float32Array(outLen);
    let g = 0;
    for (let writePos = 0; writePos < outLen; writePos += hop) {
      const readStart = earliest + offsets[g]!;
      g++;
      for (let i = 0; i < grainLen; i++) {
        const w = writePos + i;
        const r = readStart + i;
        if (w >= outLen) break;
        // Bound against this channel's own length — channel arrays are
        // typically equal-length but the type doesn't enforce it.
        if (r >= chLen) break;
        out[w] = (out[w] ?? 0) + srcCh[r]! * env[i]!;
      }
    }
    return out;
  });

  return { sampleRate: sr, channels };
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function hanning(n: number): Float32Array {
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    out[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / Math.max(1, n - 1)));
  }
  return out;
}

// Mulberry32 — same seedable PRNG used by tempoDetect tests. Six lines,
// no deps, identical output for a given seed across runtimes.
function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
